import { AlertTriangle, Info, X } from "lucide-react";
import * as React from "react";
import { hasCalls, strategyText, type CadenceFormValues, formFromBody } from "@/lib/cadence";
import {
  busiestDay, clockWords, dayStacks, planCalls, planNotes, stackRoom, type Moved, type Plan, type PlannedCall,
  type PlannedText,
} from "@/lib/cadence-plan";
import type { CadenceBody } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * What a cadence does to a lead who never picks up, drawn as it happens: a calendar of the days
 * with a cell per day shaded by its calls, the calls of the day under the pointer on an 8 am to
 * 9 pm line, and the things worth knowing in words. It is recomputed on every change, so an
 * admin sees a wait they moved move the calls.
 *
 * A text a step sends (ADR-135 in platform) is drawn beside the calls and never counted as one:
 * a green square on the day's line and a mark on its day, with a count of its own. A cadence
 * with no call in it shades its days by its texts, or its calendar would be blank.
 */

/** A phase's colour on the calendar and the day line, by its place in the cadence. */
export const PHASE_COLORS = ["bg-accent-9", "bg-gray-9", "bg-accent-6", "bg-gray-6", "bg-accent-11", "bg-gray-11"] as const;
export const phaseColor = (i: number) => PHASE_COLORS[i % PHASE_COLORS.length]!;

const SHADES = ["bg-gray-3", "bg-accent-4", "bg-accent-6", "bg-accent-8", "bg-accent-10"] as const;
const shade = (n: number) => SHADES[Math.min(n, SHADES.length - 1)]!;

const JOIN_TIMES = [
  { minute: 9 * 60, label: "9 am" },
  { minute: 13 * 60, label: "1 pm" },
  { minute: 19 * 60, label: "7 pm" },
] as const;

const MOVED: Record<Moved, string> = {
  quiet_hours: "held for the calling hours",
  daily_cap: "held by the daily cap",
  replanned: "the second call was late, so a new cycle",
};

const WEEKS_SHOWN = 13;

export function endWords(v: CadenceFormValues, plan: Plan): string {
  switch (plan.endedBy) {
    case "total_attempts":
      return `All ${plan.calls.length} calls made by day ${plan.lastDay}`;
    case "expired":
      return `Stops ${v.expire_days.trim()} days after the lead joins`;
    case "phases_over": {
      const last = v.phases[v.phases.length - 1];
      return `Stops ${last?.ends_days.trim() || plan.lastDay} days after the lead joins`;
    }
    default:
      return plan.calls.length === 0 && plan.texts.length > 0 ? "Still texting a year on" : "Still calling a year on";
  }
}

/** "3 calls", "1 text", "3 calls · 1 text", "no calls": a day's steps, or a cadence's. */
export function countWords(calls: number, texts: number, none = "no calls"): string {
  const parts = [
    ...(calls > 0 ? [`${calls} call${calls === 1 ? "" : "s"}`] : []),
    ...(texts > 0 ? [`${texts} text${texts === 1 ? "" : "s"}`] : []),
  ];
  return parts.join(" · ") || none;
}

/** How many warnings the card would show, for a lead who joins at the first time: the cadence page
 *  says so on the button that opens it, while it is closed (ACL-628). */
export function planWarnings(values: CadenceFormValues): number {
  const joinMinute = JOIN_TIMES[0].minute;
  const plan = planCalls(values, { joinMinute });
  const uncapped = values.daily_calls_per_number.trim() ? planCalls({ ...values, daily_calls_per_number: "" }, { joinMinute }) : undefined;
  return planNotes(values, plan, uncapped).filter((n) => n.tone === "warning").length;
}

export function CadencePreview({
  values, compact = false, onClose,
}: { values: CadenceFormValues; compact?: boolean; onClose?: () => void }) {
  const [expanded, setExpanded] = React.useState(false);
  const [join, setJoin] = React.useState<number>(JOIN_TIMES[0].minute);
  const plan = React.useMemo(() => planCalls(values, { joinMinute: join }), [values, join]);
  const uncapped = React.useMemo(
    () => (values.daily_calls_per_number.trim() ? planCalls({ ...values, daily_calls_per_number: "" }, { joinMinute: join }) : undefined),
    [values, join],
  );
  const notes = React.useMemo(() => planNotes(values, plan, uncapped), [values, plan, uncapped]);
  const [picked, setPicked] = React.useState(1);
  const [hovered, setHovered] = React.useState<number | null>(null);
  const day = Math.min(hovered ?? picked, Math.max(1, plan.lastDay));
  const texting = plan.texts.length > 0;
  // A cadence with no call in it is read by its texts: its days are shaded by them.
  const dialing = hasCalls(values);
  const dense = (d: number) => (dialing ? (plan.perDay[d - 1] ?? 0) : (plan.textsPerDay[d - 1] ?? 0));
  const busiest = dialing
    ? busiestDay(plan)
    : { day: 1, calls: Math.max(0, ...plan.textsPerDay) };
  const unit = dialing ? "call" : "text";
  const lastStep = Math.max(plan.calls[plan.calls.length - 1]?.day ?? 0, plan.texts[plan.texts.length - 1]?.day ?? 0);
  const shownDays = Math.min(Math.max(7, plan.lastDay), WEEKS_SHOWN * 7);
  const weeks = Math.ceil(shownDays / 7);
  const phaseOnDay = (d: number) => {
    const i = plan.phases.findIndex((p) => d >= p.fromDay && (p.toDay === null || d <= p.toDay));
    return i === -1 ? null : i;
  };

  return (
    <section
      aria-label="What a lead gets"
      className="overflow-hidden rounded-xl border border-border bg-surface shadow-xs"
    >
      <header className="flex flex-wrap items-start justify-between gap-2 border-b border-border px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-text">What a lead gets</h2>
          <p className="text-xs text-text-muted">If they never pick up.</p>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-text-subtle">Joins at</span>
          <div className="flex rounded-md border border-border p-0.5" role="group" aria-label="When the lead joins">
            {JOIN_TIMES.map((t) => (
              <button
                key={t.minute}
                type="button"
                aria-pressed={t.minute === join}
                onClick={() => setJoin(t.minute)}
                className={cn(
                  "rounded px-1.5 py-0.5 text-[11px] font-medium tabular-nums",
                  t.minute === join ? "bg-accent-9 text-white dark:text-gray-1" : "text-text-muted hover:text-text",
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close what a lead gets"
              className="ml-1 grid size-6 place-items-center rounded-md text-text-subtle hover:bg-surface-hover hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40"
            >
              <X className="size-3.5" aria-hidden />
            </button>
          )}
        </div>
      </header>

      <dl className={cn("grid divide-x divide-border border-b border-border", texting && dialing ? "grid-cols-4" : "grid-cols-3")}>
        {dialing && <Stat label="Calls in all" value={String(plan.calls.length)} />}
        {(texting || !dialing) && <Stat label="Texts in all" value={String(plan.texts.length)} />}
        <Stat label="Busiest day" value={`${busiest.calls}`} unit={busiest.calls === 1 ? unit : `${unit}s`} />
        <Stat label={texting ? "Last step" : "Last call"} value={lastStep ? `Day ${lastStep}` : "—"} />
      </dl>

      {compact && !expanded ? (
        <div className="space-y-2 px-4 py-3">
          <p className="text-xs font-medium text-text">{endWords(values, plan)}</p>
          <div className="flex gap-0.5" aria-hidden>
            {Array.from({ length: 28 }, (_, i) => (
              <span
                key={i}
                className={cn(
                  "h-4 min-w-0 flex-1 rounded-[3px]",
                  i + 1 > plan.lastDay ? "border border-dashed border-border" : shade(dense(i + 1)),
                )}
              />
            ))}
          </div>
          <button type="button" onClick={() => setExpanded(true)} className="text-xs font-medium text-accent-11 hover:underline">
            Day by day
          </button>
        </div>
      ) : (
      <>
      <div className="space-y-3 px-4 py-3">
        <p className="text-xs font-medium text-text">{endWords(values, plan)}</p>
        <div
          className="grid max-w-sm gap-1"
          style={{ gridTemplateColumns: "2.25rem repeat(7, minmax(0, 1fr))" }}
          onMouseLeave={() => setHovered(null)}
        >
          {Array.from({ length: weeks }, (_, w) => (
            <React.Fragment key={w}>
              <span className="self-center text-[10px] font-medium uppercase tracking-wide text-text-subtle">Wk {w + 1}</span>
              {Array.from({ length: 7 }, (_, k) => {
                const d = w * 7 + k + 1;
                const n = dense(d);
                const sent = plan.textsPerDay[d - 1] ?? 0;
                const beyond = d > plan.lastDay;
                const phase = beyond ? null : phaseOnDay(d);
                const selected = d === day;
                return (
                  <button
                    key={d}
                    type="button"
                    disabled={beyond}
                    onMouseEnter={() => !beyond && setHovered(d)}
                    onFocus={() => setHovered(d)}
                    onBlur={() => setHovered(null)}
                    onClick={() => setPicked(d)}
                    aria-label={`Day ${d}: ${countWords(plan.perDay[d - 1] ?? 0, sent)}`}
                    className={cn(
                      "relative flex aspect-square min-h-7 flex-col items-center justify-center overflow-hidden rounded-md text-[10px] tabular-nums transition-shadow",
                      beyond ? "border border-dashed border-border bg-transparent text-text-subtle" : shade(n),
                      !beyond && n >= 3 ? "text-white dark:text-gray-1" : "text-text-muted",
                      selected && "ring-2 ring-accent-9 ring-offset-1 ring-offset-surface",
                    )}
                  >
                    <span className="leading-none">{d}</span>
                    {n > 0 && <span className="mt-0.5 text-[11px] font-semibold leading-none">{n}</span>}
                    {/* A text beside the day's calls: a mark in its corner. */}
                    {dialing && !beyond && sent > 0 && (
                      <span className="absolute right-0.5 top-0.5 size-1.5 rounded-[2px] bg-success-9 ring-1 ring-surface" aria-hidden />
                    )}
                    {phase !== null && <span className={cn("absolute inset-x-0 bottom-0 h-0.5", phaseColor(phase))} aria-hidden />}
                  </button>
                );
              })}
            </React.Fragment>
          ))}
        </div>
        {plan.lastDay > shownDays && (
          <p className="text-[11px] text-text-subtle">
            … and {plan.lastDay - shownDays} more days,{" "}
            {countWords(
              plan.calls.filter((c) => c.day > shownDays).length,
              plan.texts.filter((t) => t.day > shownDays).length,
            )}
            .
          </p>
        )}
        <Legend plan={plan} unit={unit} texts={texting && dialing} />
      </div>

      <DayLine
        day={day}
        calls={plan.calls.filter((c) => c.day === day)}
        texts={plan.texts.filter((t) => t.day === day)}
        values={values}
      />
      </>
      )}

      {notes.length > 0 && (
        <ul className="space-y-2 border-t border-border px-4 py-3">
          {notes.map((n) => (
            <li key={n.text} className="flex gap-2 text-xs">
              {n.tone === "warning" ? (
                <AlertTriangle className="mt-px size-3.5 shrink-0 text-warning-11" aria-hidden />
              ) : (
                <Info className="mt-px size-3.5 shrink-0 text-accent-11" aria-hidden />
              )}
              <span className={n.tone === "warning" ? "text-text" : "text-text-muted"}>{n.text}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="border-t border-border bg-surface-sunken px-4 py-2 text-[11px] text-text-subtle">
        {texting ? (dialing ? "Calls and texts go out" : "Texts go out") : "Calls go out"} 8 am to 9 pm on the lead&apos;s
        clock, the campaign default. No other campaign is counted.
      </p>
    </section>
  );
}

function Stat({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div className="px-4 py-2.5">
      <dt className="text-[11px] text-text-subtle">{label}</dt>
      <dd className="text-lg font-semibold tabular-nums text-text">
        {value}
        {unit && <span className="ml-1 text-xs font-normal text-text-muted">{unit}</span>}
      </dd>
    </div>
  );
}

function Legend({ plan, unit, texts }: { plan: Plan; unit: string; texts: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-text-muted">
      <span className="inline-flex items-center gap-1">
        {[0, 1, 2, 3].map((n) => (
          <span key={n} className={cn("size-2.5 rounded-sm", shade(n))} aria-hidden />
        ))}
        <span className="ml-0.5">0–3+ {unit}s</span>
      </span>
      {texts && (
        <span className="inline-flex items-center gap-1">
          <span className="size-2 rounded-[2px] bg-success-9" aria-hidden />
          <span>a text</span>
        </span>
      )}
      {plan.phases.map((p, i) => (
        <span key={p.name} className="inline-flex items-center gap-1">
          <span className={cn("h-0.5 w-3 rounded-full", phaseColor(i))} aria-hidden />
          <span className="font-mono">{p.name}</span>
        </span>
      ))}
    </div>
  );
}

const LINE_START = 8 * 60;
const LINE_END = 21 * 60;
const TICKS = [8 * 60, 12 * 60, 16 * 60, 20 * 60];

type DayStep = { text: false; call: PlannedCall } | { text: true; call: PlannedText };

function DayLine({
  day, calls, texts, values,
}: {
  day: number;
  calls: PlannedCall[];
  texts: PlannedText[];
  values: CadenceFormValues;
}) {
  const x = (minute: number) => `${((minute - LINE_START) / (LINE_END - LINE_START)) * 100}%`;
  // The day's steps in the order they go out: a text and the call after it share a minute, and
  // the text was first.
  const steps: DayStep[] = [
    ...calls.map((call): DayStep => ({ text: false, call })),
    ...texts.map((call): DayStep => ({ text: true, call })),
  ].sort((a, b) => a.call.at - b.call.at || a.call.step - b.call.step);
  // The line is as tall as its tallest stack, so a call and its redials never climb into the
  // heading above it.
  const stacks = dayStacks(steps.map((s) => s.call.minute));
  const { step, room } = stackRoom(stacks);
  return (
    <div className="space-y-2.5 border-t border-border px-4 py-3">
      <p className="text-xs font-medium text-text">
        Day {day}
        <span className="ml-1.5 font-normal text-text-muted">
          {countWords(calls.length, texts.length, values.phases.some((p) => p.calls.some((c) => c.channel !== "sms")) ? "no calls" : "no texts")}
        </span>
      </p>
      <div className="relative mx-1" style={{ height: 44 + room }} aria-hidden>
        <div className="absolute inset-x-0 h-1 rounded-full bg-gray-3" style={{ top: 22 + room }} />
        {TICKS.map((t) => (
          <span
            key={t}
            className="absolute -translate-x-1/2 text-[10px] text-text-subtle"
            style={{ left: x(t), top: 30 + room }}
          >
            {clockWords(t).replace(":00", "")}
          </span>
        ))}
        {steps.map((s, i) => (
          <span
            key={`${s.call.at}-${i}`}
            className={cn(
              "absolute size-3 -translate-x-1/2 ring-2 ring-surface",
              // A text is a square in its own tone; a call is a dot in its phase's.
              s.text
                ? "rounded-[3px] bg-success-9"
                : s.call.strategy === "unbranded" || s.call.strategy === "rotate"
                  ? "rounded-full border-2 border-accent-9 bg-surface"
                  : cn("rounded-full", phaseColor(s.call.phase)),
            )}
            style={{ left: x(s.call.minute), top: 18 + room - (stacks[i] ?? 0) * step }}
          />
        ))}
      </div>
      {steps.length > 0 && (
        <ol className="space-y-1">
          {steps.map((s, i) => (
            <li key={`${s.call.at}-${i}`} className="flex items-baseline gap-2 text-xs">
              <span className="w-14 shrink-0 tabular-nums text-text">{clockWords(s.call.minute)}</span>
              <span className="min-w-0 text-text-muted">
                {s.text ? (
                  <>
                    <span className="font-medium text-success-11">Text {s.call.step + 1}</span> of cycle {s.call.cycle}
                  </>
                ) : (
                  <>
                    {s.call.redial > 0 ? `Redial ${s.call.redial} of call ${s.call.step + 1}` : `Call ${s.call.step + 1}`} of cycle{" "}
                    {s.call.cycle} · {strategyText(s.call.strategy).label.toLowerCase()}
                  </>
                )}
                {values.phases.length > 1 && <span className="font-mono"> · {values.phases[s.call.phase]?.name}</span>}
                {s.call.moved && <span className="text-text-subtle"> · {MOVED[s.call.moved]}</span>}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function stripEnd(v: CadenceFormValues, plan: Plan): string {
  if (plan.endedBy === "total_attempts") return `done by day ${plan.lastDay}`;
  if (plan.endedBy === "horizon") return "no end";
  const days = plan.endedBy === "expired" ? v.expire_days.trim() : v.phases[v.phases.length - 1]?.ends_days.trim();
  return `over ${days || plan.lastDay} days`;
}

/** A cadence's first three weeks in one row, for the catalog: a cell per day shaded by its
 *  calls, and how it ends. */
export function PlanStrip({ body, className }: { body: CadenceBody; className?: string }) {
  const values = React.useMemo(() => formFromBody(body, "x", "x"), [body]);
  const plan = React.useMemo(() => planCalls(values), [values]);
  const days = 21;
  const dialing = hasCalls(values);
  return (
    <div className={cn("space-y-1", className)}>
      <div className="flex gap-0.5" aria-hidden>
        {Array.from({ length: days }, (_, i) => {
          const n = dialing ? (plan.perDay[i] ?? 0) : (plan.textsPerDay[i] ?? 0);
          return (
            <span
              key={i}
              className={cn("h-3 w-2 rounded-[2px]", i + 1 > plan.lastDay ? "border border-dashed border-border" : shade(n))}
            />
          );
        })}
      </div>
      <p className="text-[11px] text-text-muted">
        {dialing ? countWords(plan.calls.length, plan.texts.length, "0 calls") : countWords(0, plan.texts.length, "0 texts")} ·{" "}
        {stripEnd(values, plan)}
      </p>
    </div>
  );
}

